import { UrlEventType, UrlParameter, VisitType } from 'src/requester';
import { CustomDataType, IKameleoonData, KameleoonData } from './types';
import { TrackingStatus } from 'src/types';

/**
 * @class
 * CustomData - a class for creating an instance for user's custom data
 * */
export class CustomData implements IKameleoonData {
  private _status: TrackingStatus;
  private index: number;
  private value: string[];
  private isIdentifier: boolean;

  /**
   * @param {number} index - an index of custom data to be stored under in a state, an index of custom data can be specified in `Advanced Tools` section of Kameleoon Application
   * @param {string[]} value - custom value to store under the specified id, value can be anything but has to be stringified to match the `string` type. *Note* value is variadic parameter and can be used as follows
   * @example
   * ```ts
   * // - Single value
   * const customData = new CustomData(0, 'value_1');
   * // - Variadic number of values
   * const customData = new CustomData(0, 'value_1', 'value_2', 'value_3');
   * // - Array of values
   * const values = ['value_1', 'value_2', 'value_3'];
   * const customData = new CustomData(0, ...values);
   * ```
   * */
  constructor(index: number, ...value: string[]) {
    this.index = index;
    this.value = value;
    this._status = TrackingStatus.Unsent;
    this.isIdentifier = false;
  }

  get url(): string {
    // --- Note ---
    // If SDK is used in vanilla JS codebase, then you're also able to create an instance
    // with no required data, we don't want send anything to tracking in that case
    if (typeof this.index !== 'number') {
      return '';
    }

    const uniqueValues = [...new Set(this.value)];
    let resultValue: Record<string, 1> = {};

    // --- Note ---
    // Final value should look like this `{ "value_1": 1, "value_2": 1, "value_3": 1 }`
    // With no duplicates
    uniqueValues.forEach((value) => {
      resultValue[value] = 1;
    });

    let identifierParameter = '';

    if (this.isIdentifier) {
      identifierParameter = UrlParameter.MappingIdentifier + String(true);
    }

    return (
      UrlEventType.CustomData +
      UrlParameter.Index +
      this.index +
      UrlParameter.ValuesCountMap +
      encodeURIComponent(JSON.stringify(resultValue)) +
      UrlParameter.Overwrite +
      String(true) +
      identifierParameter
    );
  }

  get data(): CustomDataType {
    return {
      index: this.index,
      value: this.value,
      type: KameleoonData.CustomData,
      isIdentifier: this.isIdentifier,
      status: this.status,
    };
  }

  get status(): TrackingStatus {
    if (this._isMappingIdentifier) {
      return TrackingStatus.Unsent;
    }

    return this._status;
  }

  /**
   * @private
   * @method _fromRaw - an internal method for creating a CustomData instance from raw data
   * @param {CustomDataType} data - a raw data
   * @return {CustomData} a CustomData instance
   * */
  public static _fromRaw(data: CustomDataType): CustomData {
    const { index, value, status, isIdentifier } = data;
    const customData = new CustomData(index, ...value);

    customData.status = status;
    customData._isMappingIdentifier = isIdentifier;

    return customData;
  }

  /**
   * @property isMappingIdentifier - signifying if the data is a mapping identifier
   * @private
   * */
  set _isMappingIdentifier(value: boolean) {
    this.isIdentifier = value;
  }

  set status(status: TrackingStatus) {
    this._status = status;
  }

  /**
   * @private
   * @method _updateFromVisit - an internal method for updating a list of custom data from a visit
   * @param {VisitType} visit - a visit
   * */
  public static _updateFromVisit(
    visit: VisitType,
    dataMap: Map<number, CustomData>,
  ): void {
    if (visit.customDataEvents?.length) {
      for (let i = visit.customDataEvents.length - 1; i >= 0; i--) {
        const { index, valuesCountMap } = visit.customDataEvents[i].data;

        if (!dataMap.has(index)) {
          dataMap.set(
            index,
            new CustomData(index, ...Object.keys(valuesCountMap)),
          );
        }
      }
    }
  }
}
